import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import type { FunctionComponent } from 'react';
import { Fragment, useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import {
    IconAnonymize,
    IconDocument,
    IconFileDownload,
    IconLogout,
    IconPeople,
    IconSettings,
    IconShield,
} from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { theme } from '~/ui/theme/cobrainer-theme';

import { sidebar } from '../../../company-settings/constants';
import { buildPersonalPreferencesSettingsUrl } from '../../../company-settings/route-config';
import { getUserInitials } from '../../../design/components/avatar/common';
import { getUserName } from '../../common/user';
import { route as profilePageRoute } from '../../pages/profile/constants';
import { route as settingsPageRoute } from '../../pages/settings/constants';
import { PrivacyTermsDialog } from '../../pages/settings/Dialogs';
import { CustomThemeProvider } from '../../theming';

const UserButton = styled(Button)(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

const UserName = styled.span(({ theme }) => ({
    color: theme.colors.black[800],
    ...theme.typography.body.S.regular,
    whiteSpace: 'nowrap',
}));

const AnonymizeMenuItem: FunctionComponent = () => {
    const { actions, applicationState, translate } = useContext(ApplicationContext);
    const { userIsAnonymized } = applicationState;

    const itemText = translate(
        userIsAnonymized
            ? 'components.anonymize-button.user-names-show'
            : 'components.anonymize-button.user-names-hide',
    );

    return (
        <DropdownMenuItem
            aria-label={translate('usermenu.anonymize')}
            onSelect={() => {
                actions.toggleAnonymization();
            }}
        >
            <IconAnonymize size={16} />
            {itemText}
        </DropdownMenuItem>
    );
};

const privacyStatementDialogSearchString = new URLSearchParams({
    [settingsPageRoute.searchParams.openPrivacyStatement]: 'true',
}).toString();

const Avatar = styled('div')(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.gray[300],
    borderRadius: '100%',
    color: theme.colors.gray[800],
    display: 'flex',
    ...theme.typography.body.S.regular,
    height: '2rem',
    justifyContent: 'center',
    width: '2rem',
}));

export const UserMenuV2: FunctionComponent = () => {
    const { actions, applicationState } = useApplicationContext();
    const translate = useTranslate();
    const { myUserProfile } = applicationState;
    const isNewUserSettingsEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('new-user-settings');
    const fullName = getUserName(myUserProfile);
    const userManualUrlHref = applicationState.customization.get('uiConfiguration').get('userManualUrl').get('href');
    const [open, setOpen] = useState(false);

    const canSeeHideNamesButton = myUserProfile
        .map((user) => user.roles?.includes('recruiter') === true || user.roles?.includes('hiring_manager') === true)
        .unwrapOr(false);

    const handleOpenChange = (newOpen: boolean): void => {
        setOpen(newOpen);
    };

    const handleLogout = (): void => {
        actions.logout();
        setOpen(false);
    };

    return (
        <ThemeProvider theme={theme}>
            {/** biome-ignore lint/complexity/noUselessFragments: suppressed due to migration */}
            <Fragment>
                <CustomThemeProvider>
                    <PrivacyTermsDialog />
                </CustomThemeProvider>
                <DropdownMenu open={open} onOpenChange={handleOpenChange}>
                    <Tooltip>
                        <Tooltip.Trigger asChild={true}>
                            <DropdownMenuTrigger asChild={true}>
                                <UserButton variant="ghost">
                                    <UserName>{fullName}</UserName>
                                    <Avatar>
                                        {getUserInitials(
                                            myUserProfile.get('firstName').unwrapOr(''),
                                            myUserProfile.get('lastName').unwrapOr(''),
                                        )}
                                    </Avatar>
                                </UserButton>
                            </DropdownMenuTrigger>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content>{translate('usermenu.tooltip')}</Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip>
                    <DropdownMenuContent align="end" alignOffset={10} style={{ zIndex: 2000 }}>
                        <DropdownMenuItem
                            aria-label={translate('usermenu.my-profile')}
                            onSelect={() => {
                                actions.navigateTo(profilePageRoute.patterns.usersMe);
                                setOpen(false);
                            }}
                        >
                            <IconPeople size={16} />
                            {translate('usermenu.my-profile')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            aria-label={translate('pages.settings.data-privacy.title')}
                            onSelect={() => {
                                actions.navigateTo(`${location.pathname}?${privacyStatementDialogSearchString}`);
                                setOpen(false);
                            }}
                        >
                            <IconShield size={16} />
                            {translate('pages.settings.data-privacy.title')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            aria-label={translate('common.imprint')}
                            onSelect={() => {
                                globalThis.open('https://www.cobrainer.com/imprint', '_blank');
                                setOpen(false);
                            }}
                        >
                            <IconDocument size={16} />
                            {translate('common.imprint')}
                        </DropdownMenuItem>
                        {canSeeHideNamesButton === true && <AnonymizeMenuItem />}
                        <DropdownMenuItem
                            aria-label={translate('usermenu.account-settings')}
                            onSelect={() => {
                                actions.navigateTo(
                                    isNewUserSettingsEnabled
                                        ? buildPersonalPreferencesSettingsUrl(sidebar.personalPreferences.account)
                                        : settingsPageRoute.patterns.settings,
                                );
                                setOpen(false);
                            }}
                        >
                            <IconSettings size={16} />
                            {translate('usermenu.account-settings')}
                        </DropdownMenuItem>
                        {userManualUrlHref.isJust && (
                            <Fragment>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    aria-label={translate('common.download-manual')}
                                    onSelect={() => {
                                        globalThis.open(userManualUrlHref.value, '_blank');
                                        setOpen(false);
                                    }}
                                >
                                    <IconFileDownload size={16} />
                                    {translate('common.download-manual')}
                                </DropdownMenuItem>
                            </Fragment>
                        )}
                        <DropdownMenuSeparator />
                        {!applicationState.hideLogoutButtons && (
                            <DropdownMenuItem aria-label={translate('common.logout')} onSelect={handleLogout}>
                                <IconLogout size={16} />
                                {translate('common.logout')}
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </Fragment>
        </ThemeProvider>
    );
};
